import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Users, Sparkles, Building2, CheckCircle2, XCircle, AlertCircle, Eye, Trash2, Database, AlertTriangle, Inbox, Phone, Mail, Image as ImageIcon, RefreshCw, ExternalLink, BadgeCheck, Briefcase, Star, Banknote, Handshake, Plus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import AdminPricingTab from "@/components/AdminPricingTab";
import AdminContactsTab from "@/components/AdminContactsTab";
import AdminInvestorPipelinesTab from "@/components/AdminInvestorPipelinesTab";
import AdminConciergePurchasesTab from "@/components/AdminConciergePurchasesTab";
import AdminTLTBContactsTab from "@/components/AdminTLTBContactsTab";
import AdminTLTBShareAnalyticsTab from "@/components/AdminTLTBShareAnalyticsTab";
import AdminPayoutsTab from "@/components/AdminPayoutsTab";
import AdminPlatformSettingsTab from "@/components/AdminPlatformSettingsTab";
import { api, fmtFJD, formatApiError } from "@/lib/api";

export default function Admin() {
    const [stats, setStats] = useState(null);
    const [pending, setPending] = useState([]);
    const [users, setUsers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [active, setActive] = useState(null);
    const [decision, setDecision] = useState({ decision: "approved", score: 80, comment: "" });

    // Test data management
    const [testSummary, setTestSummary] = useState(null);
    const [wipeOpen, setWipeOpen] = useState(false);
    const [wipeConfirm, setWipeConfirm] = useState("");
    const [wiping, setWiping] = useState(false);

    // Leads
    const [leads, setLeads] = useState([]);
    const [leadStats, setLeadStats] = useState(null);
    const [leadFilter, setLeadFilter] = useState("all");
    const [leadType, setLeadType] = useState("all"); // "all" | "jv" | "consultation"
    const [magicLinkModal, setMagicLinkModal] = useState(null); // {magic_link, jv_id, lead_name}

    // Image health
    const [imageHealth, setImageHealth] = useState(null); // { summary, broken } from current run
    const [imageHealthLast, setImageHealthLast] = useState(null); // last saved run
    const [imageHealthRunning, setImageHealthRunning] = useState(false);

    // Agents
    const [agents, setAgents] = useState([]);
    const [agentListings, setAgentListings] = useState({}); // { agentId: [listings] }

    // Lane breakdown (audience composition)
    const [laneBreakdown, setLaneBreakdown] = useState(null);

    // Investor matcher (Phase 4)
    const [matcher, setMatcher] = useState({ asset_type: "jv", asset_id: "", result: null, loading: false });

    // Deal Pipeline (Phase 6)
    const [dealPipeline, setDealPipeline] = useState(null);
    const [deals, setDeals] = useState([]);
    const [dealStatusFilter, setDealStatusFilter] = useState("all");
    const [createDealOpen, setCreateDealOpen] = useState(false);
    const [creatingDeal, setCreatingDeal] = useState(false);
    const [bulaBrief, setBulaBrief] = useState(null);
    const [briefRunning, setBriefRunning] = useState(false);
    const [dealForm, setDealForm] = useState({
        title: "",
        notes: "",
        asset_type: "jv",
        asset_id: "",
        intro_source: "manual",
        seller_name: "",
        buyer_name: "",
        region: "",
        deal_value_fjd: 0,
        agreed_fee_pct: 5,
        agreed_fee_override: "",  // empty = use auto-compute; non-empty = override
        referrer_user_id: "",
        referrer_share_pct: 0,
        status: "introduced",
    });

    const refresh = () => {
        api.get("/admin/stats").then(({ data }) => setStats(data));
        api.get("/admin/jv/queue?status=pending").then(({ data }) => setPending(data.items || []));
        api.get("/admin/users").then(({ data }) => setUsers(data.items || []));
        api.get("/admin/audit").then(({ data }) => setAudit(data.items || []));
        api.get("/admin/test-data/summary").then(({ data }) => setTestSummary(data)).catch(() => {});
        api.get("/admin/jv-leads/stats").then(({ data }) => setLeadStats(data)).catch(() => {});
        const qs = new URLSearchParams();
        if (leadFilter !== "all") qs.set("status", leadFilter);
        if (leadType !== "all") qs.set("lead_type", leadType);
        const params = qs.toString() ? `?${qs.toString()}` : "";
        api.get(`/admin/jv-leads${params}`).then(({ data }) => setLeads(data.items || [])).catch(() => {});
        api.get("/admin/image-health/last").then(({ data }) => setImageHealthLast(data?.summary ? data : null)).catch(() => {});
        api.get("/admin/agents").then(({ data }) => setAgents(data.items || [])).catch(() => {});
        api.get("/admin/users/lane-breakdown").then(({ data }) => setLaneBreakdown(data)).catch(() => {});
        api.get("/admin/deals/pipeline").then(({ data }) => setDealPipeline(data)).catch(() => {});
        const dq = dealStatusFilter !== "all" ? `?status=${dealStatusFilter}` : "";
        api.get(`/admin/deals${dq}`).then(({ data }) => setDeals(data.items || [])).catch(() => {});
        api.get("/admin/bula-brief/preview").then(({ data }) => setBulaBrief(data)).catch(() => {});
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [leadFilter, leadType, dealStatusFilter]);

    const submitDecision = async () => {
        try {
            await api.post(`/admin/jv/${active.id}/decision`, decision);
            toast.success(`Joint Venture ${decision.decision}`);
            setActive(null);
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const runImageHealthCheck = async () => {
        setImageHealthRunning(true);
        try {
            const { data } = await api.get("/admin/image-health");
            setImageHealth(data);
            setImageHealthLast({ summary: data.summary, sample_broken: data.broken.slice(0, 10), created_at: data.summary.checked_at });
            const broken = data.summary?.broken ?? 0;
            if (broken === 0) toast.success(`All ${data.summary.total_references} image refs are healthy.`);
            else toast.error(`${broken} broken image${broken === 1 ? "" : "s"} found. Review below.`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setImageHealthRunning(false);
        }
    };

    const verifyAgent = async (userId, verified) => {
        try {
            await api.patch(`/admin/users/${userId}/verify-agent?verified=${verified}`);
            toast.success(verified ? "Agent verified." : "Verification removed.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const featureListing = async (listingId, isFeatured) => {
        try {
            await api.patch(`/admin/listings/${listingId}/feature`, { is_featured: isFeatured });
            toast.success(isFeatured ? "Listing featured." : "Removed from featured.");
            const ids = Object.keys(agentListings);
            for (const aid of ids) {
                const list = agentListings[aid] || [];
                if (list.find((l) => l.id === listingId)) {
                    const refreshed = list.map((l) => l.id === listingId ? { ...l, is_featured: isFeatured } : l);
                    setAgentListings((prev) => ({ ...prev, [aid]: refreshed }));
                }
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const loadAgentListings = async (agentId) => {
        if (agentListings[agentId]) return;
        try {
            const { data } = await api.get(`/agents/${agentId}`);
            setAgentListings((prev) => ({ ...prev, [agentId]: data.listings || [] }));
        } catch (_e) { /* ignore */ }
    };

    const runMatcher = async () => {
        if (!matcher.asset_id.trim()) return toast.error("Paste an asset ID first");
        setMatcher((m) => ({ ...m, loading: true, result: null }));
        try {
            const { data } = await api.get(`/admin/match/${matcher.asset_type}/${matcher.asset_id.trim()}?limit=25`);
            setMatcher((m) => ({ ...m, loading: false, result: data }));
            toast.success(`${data.total_matched} investor${data.total_matched === 1 ? "" : "s"} matched`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Match failed");
            setMatcher((m) => ({ ...m, loading: false, result: null }));
        }
    };

    const submitDeal = async () => {
        if (!dealForm.title.trim()) return toast.error("Deal title required");
        setCreatingDeal(true);
        try {
            const overrideRaw = String(dealForm.agreed_fee_override).trim();
            const payload = {
                title: dealForm.title,
                notes: dealForm.notes,
                asset_type: dealForm.asset_type,
                asset_id: dealForm.asset_id,
                intro_source: dealForm.intro_source,
                seller_name: dealForm.seller_name,
                buyer_name: dealForm.buyer_name,
                region: dealForm.region,
                deal_value_fjd: parseFloat(dealForm.deal_value_fjd) || 0,
                agreed_fee_pct: parseFloat(dealForm.agreed_fee_pct) || 0,
                referrer_user_id: dealForm.referrer_user_id?.trim() || null,
                referrer_share_pct: parseFloat(dealForm.referrer_share_pct) || 0,
                status: dealForm.status,
            };
            if (overrideRaw !== "") {
                const ov = parseFloat(overrideRaw);
                if (!isNaN(ov) && ov >= 0) payload.agreed_fee_fjd = ov;
            }
            await api.post("/admin/deals", payload);
            toast.success("Deal created");
            setCreateDealOpen(false);
            setDealForm({
                title: "", notes: "", asset_type: "jv", asset_id: "", intro_source: "manual",
                seller_name: "", buyer_name: "", region: "",
                deal_value_fjd: 0, agreed_fee_pct: 5, agreed_fee_override: "",
                referrer_user_id: "", referrer_share_pct: 0,
                status: "introduced",
            });
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to create deal");
        } finally {
            setCreatingDeal(false);
        }
    };

    const updateDealStatus = async (dealId, status) => {
        try {
            await api.patch(`/admin/deals/${dealId}/status`, { status });
            toast.success(`Deal moved to ${status.replace("_", " ")}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const deleteDeal = async (dealId) => {
        if (!window.confirm("Delete this deal permanently?")) return;
        try {
            await api.delete(`/admin/deals/${dealId}`);
            toast.success("Deal deleted");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const runBulaBrief = async () => {
        if (!window.confirm("Send the Bula Brief digest now? This queues a mock-Resend email per recipient.")) return;
        setBriefRunning(true);
        try {
            const { data } = await api.post("/admin/bula-brief/run");
            toast.success(`Bula Brief queued — ${data.queued} email${data.queued === 1 ? "" : "s"} (top Joint Ventures: ${data.top_jvs_count}, headline: ${data.has_headline ? "yes" : "no"})`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to send Bula Brief");
        } finally {
            setBriefRunning(false);
        }
    };

    const wipeTestData = async () => {
        if (wipeConfirm !== "WIPE") {
            toast.error("Type WIPE to confirm");
            return;
        }
        setWiping(true);
        try {
            const { data } = await api.post("/admin/test-data/wipe");
            const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
            toast.success(`Wiped ${total} test record(s) across ${Object.keys(data.deleted).length} collections`);
            setWipeOpen(false);
            setWipeConfirm("");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setWiping(false);
        }
    };

    const updateLeadStatus = async (leadId, status, note = "") => {
        try {
            const { data } = await api.patch(`/admin/jv-leads/${leadId}/status`, { status, admin_note: note });
            toast.success(`Lead marked as ${status.replace("_", " ")}`);
            if (status === "won" && data.automation?.magic_link) {
                setMagicLinkModal({
                    magic_link: data.automation.magic_link,
                    jv_id: data.automation.jv_id,
                    user_created: data.automation.user_created,
                    expires_at: data.automation.expires_at,
                    lead_name: data.full_name || "the prospect",
                });
            }
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };


    return (
        <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="h-7 w-7 text-coral" />
                <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold">Admin Control Center</div>
            </div>
            <h1 className="font-display text-4xl font-bold mb-8">Platform Operations</h1>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
                {[
                    { label: "Users", value: stats?.users, icon: Users, color: "text-ocean" },
                    { label: "Listings", value: stats?.listings, icon: Building2, color: "text-fiji-green" },
                    { label: "Joint Ventures Total", value: stats?.jvs_total, icon: Sparkles, color: "text-coral" },
                    { label: "Joint Ventures Pending", value: stats?.jvs_pending, icon: AlertCircle, color: "text-coral" },
                    { label: "Joint Ventures Approved", value: stats?.jvs_approved, icon: CheckCircle2, color: "text-fiji-green" },
                    { label: "Joint Ventures Rejected", value: stats?.jvs_rejected, icon: XCircle, color: "text-destructive" },
                    { label: "Active Listings", value: stats?.active_listings, icon: Building2, color: "text-turquoise" },
                    { label: "Messages", value: stats?.messages, icon: Eye, color: "text-ocean" },
                ].map((s) => (
                    <Card key={s.label} className="p-4">
                        <s.icon className={`h-4 w-4 mb-1 ${s.color}`} />
                        <div className="font-display text-2xl font-bold">{s.value ?? "—"}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">{s.label}</div>
                    </Card>
                ))}
            </div>

            <Tabs defaultValue="queue">
                <TabsList>
                    <TabsTrigger value="queue" data-testid="admin-tab-queue">Joint Venture Queue ({pending.length})</TabsTrigger>
                    <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads ({leadStats?.new || 0} new)</TabsTrigger>
                    <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
                    <TabsTrigger value="testdata" data-testid="admin-tab-testdata">Test Data</TabsTrigger>
                    <TabsTrigger value="imagehealth" data-testid="admin-tab-imagehealth">Image Health</TabsTrigger>
                    <TabsTrigger value="agents" data-testid="admin-tab-agents">Agents</TabsTrigger>
                    <TabsTrigger value="matcher" data-testid="admin-tab-matcher">Investor Match</TabsTrigger>
                    <TabsTrigger value="deals" data-testid="admin-tab-deals">Deal Pipeline ({dealPipeline?.counts?.total || 0})</TabsTrigger>
                    <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing &amp; Sync</TabsTrigger>
                    <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts</TabsTrigger>
                    <TabsTrigger value="pipelines" data-testid="admin-tab-pipelines">Investor Pipelines</TabsTrigger>
                    <TabsTrigger value="concierge" data-testid="admin-tab-concierge">Concierge</TabsTrigger>
                    <TabsTrigger value="tltb" data-testid="admin-tab-tltb">TLTB Contacts</TabsTrigger>
                    <TabsTrigger value="tltb-analytics" data-testid="admin-tab-tltb-analytics">TLTB Shares</TabsTrigger>
                    <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
                    <TabsTrigger value="settings" data-testid="admin-tab-settings">Platform Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="queue" className="mt-6">
                    {pending.length === 0 ? (
                        <Card className="p-12 text-center">
                            <CheckCircle2 className="h-10 w-10 mx-auto text-fiji-green mb-3" />
                            <p className="font-app text-muted-foreground">No Joint Venture submissions awaiting review. Vinaka!</p>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {pending.map((jv) => (
                                <Card key={jv.id} data-testid={`pending-jv-${jv.id}`} className="p-6">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <Badge className="bg-coral/15 text-coral border-0 uppercase text-[10px]">Pending</Badge>
                                                <Badge variant="outline" className="text-[10px] uppercase">{jv.project_type}</Badge>
                                                <Badge variant="outline" className="text-[10px] uppercase">{jv.risk_profile}</Badge>
                                            </div>
                                            <h3 className="font-display text-xl font-bold mb-1">{jv.title}</h3>
                                            <p className="text-sm text-muted-foreground font-app line-clamp-2 mb-2">{jv.project_vision}</p>
                                            <div className="text-xs font-app text-muted-foreground">
                                                {jv.location_region}, {jv.location_island} · Capital {fmtFJD(jv.capital_required_fjd)} · ROI {jv.roi_estimate_pct}% · {jv.timeline_months} mo
                                            </div>
                                            <div className="text-xs font-app text-muted-foreground mt-1">Submitted by: {jv.owner_name}</div>
                                        </div>
                                        <Button onClick={() => { setActive(jv); setDecision({ decision: "approved", score: 80, comment: "" }); }} data-testid={`review-jv-${jv.id}`} className="bg-ocean-green text-white">
                                            Review →
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="leads" className="mt-6">
                    <Card className="p-5 mb-4 bg-gradient-to-r from-coral/10 to-fiji-green/10 border-coral/30">
                        <div className="flex items-center gap-3 mb-3">
                            <Inbox className="h-5 w-5 text-coral" />
                            <h2 className="font-display text-lg font-bold">Lead Generation Queue</h2>
                        </div>
                        {leadStats && (
                            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-3">
                                {[
                                    { k: "total", label: "Total" },
                                    { k: "new", label: "New" },
                                    { k: "contacted", label: "Contacted" },
                                    { k: "qualified", label: "Qualified" },
                                    { k: "proposal_sent", label: "Proposal" },
                                    { k: "won", label: "Won" },
                                    { k: "lost", label: "Lost" },
                                ].map((s) => (
                                    <button
                                        key={s.k}
                                        onClick={() => setLeadFilter(s.k === "total" ? "all" : s.k)}
                                        data-testid={`lead-filter-${s.k}`}
                                        className={`text-left p-2 rounded-md border transition ${
                                            (leadFilter === s.k || (s.k === "total" && leadFilter === "all"))
                                                ? "bg-ocean-green text-white border-transparent"
                                                : "bg-card border-border hover:border-coral"
                                        }`}
                                    >
                                        <div className="font-display text-xl font-bold">{leadStats[s.k] || 0}</div>
                                        <div className="text-[10px] uppercase tracking-wider font-app opacity-80">{s.label}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap" data-testid="lead-type-filter">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Type:</span>
                            {[
                                { k: "all", label: `All (${leadStats?.total || 0})` },
                                { k: "jv", label: `Joint Venture valuations (${leadStats?.jv || 0})` },
                                { k: "consultation", label: `Consultations (${leadStats?.consultation || 0})` },
                            ].map((opt) => (
                                <button
                                    key={opt.k}
                                    onClick={() => setLeadType(opt.k)}
                                    data-testid={`lead-type-${opt.k}`}
                                    className={`px-3 py-1 rounded-full text-xs font-app transition ${leadType === opt.k ? "bg-coral text-white" : "bg-secondary hover:bg-secondary/80"}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </Card>

                    {leads.length === 0 ? (
                        <Card className="p-12 text-center">
                            <Inbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                            <p className="font-app text-muted-foreground">No leads {leadFilter !== "all" ? `with status "${leadFilter}"` : "yet"}.</p>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {leads.map((lead) => (
                                <Card key={lead.id} data-testid={`lead-${lead.id}`} className="p-5">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <Badge className={`uppercase text-[10px] ${
                                                    lead.status === "new" ? "bg-coral/15 text-coral" :
                                                    lead.status === "contacted" ? "bg-turquoise/15 text-ocean dark:text-turquoise" :
                                                    lead.status === "qualified" ? "bg-fiji-green/15 text-fiji-green" :
                                                    lead.status === "proposal_sent" ? "bg-ocean/15 text-ocean dark:text-turquoise" :
                                                    lead.status === "won" ? "bg-fiji-green text-white" :
                                                    "bg-destructive/15 text-destructive"
                                                }`}>{lead.status?.replace("_", " ")}</Badge>
                                                <Badge className={`uppercase text-[10px] ${lead.lead_type === "consultation" ? "bg-coral text-white" : "bg-fiji-green/15 text-fiji-green"}`}>
                                                    {lead.lead_type === "consultation" ? "Consultation" : "Joint Venture valuation"}
                                                </Badge>
                                                <Badge variant="outline" className="text-[10px]">{lead.source || "direct"}</Badge>
                                                <span className="text-xs font-app text-muted-foreground">{new Date(lead.created_at).toLocaleString()}</span>
                                            </div>
                                            <h3 className="font-display text-lg font-bold mb-1">{lead.full_name}</h3>
                                            <div className="grid sm:grid-cols-2 gap-1 text-xs font-app text-muted-foreground mb-2">
                                                <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> <a href={`mailto:${lead.email}`} className="hover:text-coral">{lead.email}</a></span>
                                                <span className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> <a href={`tel:${lead.phone}`} className="hover:text-coral">{lead.phone}</a></span>
                                            </div>
                                            {lead.lead_type === "consultation" ? (
                                                <div className="text-sm font-app">
                                                    {lead.topic && <span className="font-semibold">Topic: <span className="capitalize">{String(lead.topic).replace(/_/g, " ")}</span></span>}
                                                    {lead.services_interest?.length > 0 && (
                                                        <span className="text-muted-foreground"> · interested in: {lead.services_interest.join(", ")}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-sm font-app">
                                                    {lead.land_acres ? <strong>{lead.land_acres} ac </strong> : null}
                                                    {lead.location_region ? <>in {lead.location_region}{lead.location_island ? `, ${lead.location_island}` : ""}</> : null}
                                                    {lead.asking_price_fjd ? <span className="ml-2 text-fiji-green">· asking {fmtFJD(lead.asking_price_fjd)}</span> : null}
                                                </div>
                                            )}
                                            {lead.message && (
                                                <p className="text-sm italic font-app text-muted-foreground mt-2 border-l-2 border-border pl-3">{lead.message}</p>
                                            )}
                                            {lead.admin_notes?.length > 0 && (
                                                <div className="mt-2 p-2 bg-secondary/40 rounded text-xs font-app">
                                                    <div className="font-semibold mb-1">Admin notes:</div>
                                                    {lead.admin_notes.slice(-2).map((n, i) => <div key={i} className="text-muted-foreground">• [{n.status}] {n.note}</div>)}
                                                </div>
                                            )}
                                        </div>
                                        <select
                                            data-testid={`lead-status-select-${lead.id}`}
                                            value={lead.status}
                                            onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                                            className="h-9 rounded-md border border-border bg-background px-3 text-xs font-app"
                                        >
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="qualified">Qualified</option>
                                            <option value="proposal_sent">Proposal Sent</option>
                                            <option value="won">Won</option>
                                            <option value="lost">Lost</option>
                                        </select>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="users" className="mt-6">
                    {laneBreakdown && (
                        <div className="mb-6" data-testid="lane-breakdown">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Total users</div><div className="font-display text-2xl font-bold tabular-nums">{laneBreakdown.totals.users}</div></Card>
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Phone verified</div><div className="font-display text-2xl font-bold tabular-nums text-fiji-green">{laneBreakdown.totals.phone_verified}</div></Card>
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">New (7d)</div><div className="font-display text-2xl font-bold tabular-nums text-coral">{laneBreakdown.totals.new_7d}</div></Card>
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Multi-lane</div><div className="font-display text-2xl font-bold tabular-nums">{laneBreakdown.totals.multi_lane}</div></Card>
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Verified agents</div><div className="font-display text-2xl font-bold tabular-nums text-ocean dark:text-turquoise">{laneBreakdown.totals.verified_agents}</div></Card>
                                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">No lane</div><div className="font-display text-2xl font-bold tabular-nums text-muted-foreground">{laneBreakdown.totals.no_lane}</div></Card>
                            </div>
                            <Card className="p-5">
                                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-app font-semibold mb-3">Audience by service lane</div>
                                <div className="space-y-2">
                                    {laneBreakdown.lanes.map((l) => {
                                        const max = Math.max(...laneBreakdown.lanes.map(x => x.total), 1);
                                        const pct = (l.total / max) * 100;
                                        const labelMap = {
                                            landowner: "Landowners",
                                            agent: "Real estate agents",
                                            investor: "Investors",
                                            business_seller: "Business sellers",
                                            business_buyer: "Business buyers",
                                            consultation: "Consultation requests",
                                        };
                                        return (
                                            <div key={l.lane} data-testid={`lane-row-${l.lane}`} className="flex items-center gap-3">
                                                <div className="w-44 text-sm font-app font-medium flex-shrink-0">{labelMap[l.lane] || l.lane}</div>
                                                <div className="flex-1 h-7 rounded bg-secondary/50 relative overflow-hidden">
                                                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-fiji-green/80 to-ocean/80 dark:to-turquoise/80" style={{ width: `${pct}%` }} />
                                                </div>
                                                <div className="flex items-baseline gap-2 w-56 flex-shrink-0 text-xs font-app tabular-nums">
                                                    <span className="font-display font-bold text-base text-foreground">{l.total}</span>
                                                    <span className="text-muted-foreground text-[11px]">· {l.phone_verified} verified · +{l.new_7d} this week</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        </div>
                    )}
                    <Card className="overflow-x-auto">
                        <table className="w-full text-sm font-app">
                            <thead className="bg-secondary/50 text-muted-foreground uppercase text-xs">
                                <tr>
                                    <th className="text-left px-4 py-3">Name</th>
                                    <th className="text-left px-4 py-3">Email</th>
                                    <th className="text-left px-4 py-3">Phone</th>
                                    <th className="text-left px-4 py-3">Roles</th>
                                    <th className="text-left px-4 py-3">Verified</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {users.map((u) => (
                                    <tr key={u.id} className="hover:bg-secondary/20">
                                        <td className="px-4 py-3 font-medium">{u.full_name}</td>
                                        <td className="px-4 py-3">{u.email}</td>
                                        <td className="px-4 py-3">{u.phone}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1 flex-wrap">
                                                {u.roles?.map((r) => <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.phone_verified ? <CheckCircle2 className="h-4 w-4 text-fiji-green" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="audit" className="mt-6">
                    <Card className="overflow-x-auto">
                        <table className="w-full text-sm font-app">
                            <thead className="bg-secondary/50 text-muted-foreground uppercase text-xs">
                                <tr>
                                    <th className="text-left px-4 py-3">When</th>
                                    <th className="text-left px-4 py-3">Action</th>
                                    <th className="text-left px-4 py-3">Target</th>
                                    <th className="text-left px-4 py-3">Actor</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {audit.map((a) => (
                                    <tr key={a.id}>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                                        <td className="px-4 py-3 font-medium">{a.action}</td>
                                        <td className="px-4 py-3 text-xs">{a.target_type}: {a.target_id?.slice(0, 8)}…</td>
                                        <td className="px-4 py-3 text-xs">{a.actor_id?.slice(0, 8)}…</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="testdata" className="mt-6">
                    <Card className="p-6 mb-6 bg-coral/5 border-coral/30">
                        <div className="flex items-start gap-3 mb-4">
                            <AlertTriangle className="h-6 w-6 text-coral flex-shrink-0" />
                            <div>
                                <h2 className="font-display text-xl font-bold">Test / Seeded Data Manager</h2>
                                <p className="text-sm font-app text-muted-foreground">
                                    All seed users, listings, Joint Ventures, and case studies created during build are tagged <code className="px-1.5 py-0.5 bg-secondary rounded text-xs">is_test_data: true</code>.
                                    Use this panel to view counts and wipe everything when you're ready to launch with real deals.
                                </p>
                            </div>
                        </div>

                        <h3 className="font-display text-base font-semibold mb-2 mt-4">Test data summary</h3>
                        {!testSummary ? (
                            <p className="text-sm font-app text-muted-foreground">Loading…</p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="test-data-summary">
                                {Object.entries(testSummary).map(([key, val]) => {
                                    const TEST_DATA_LABELS = {
                                        users: "Users",
                                        listings: "Listings",
                                        jv_opportunities: "Joint Venture Opportunities",
                                        case_studies: "Case Studies",
                                        favorites: "Favorites",
                                        messages: "Messages",
                                        threads: "Threads",
                                        newsletter_subscribers: "Newsletter Subscribers",
                                        investor_watchlist: "Investor Watchlist",
                                        payment_transactions: "Payment Transactions",
                                        jv_leads: "Joint Venture Leads",
                                        magic_invites: "Magic Invites",
                                        deals: "Deals",
                                    };
                                    return (
                                        <Card key={key} className="p-3">
                                            <Database className="h-3.5 w-3.5 text-coral mb-1" />
                                            <div className="font-display text-2xl font-bold">{val}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">
                                                {TEST_DATA_LABELS[key] || key.replace(/_/g, " ")}
                                            </div>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}

                        <div className="mt-6 p-4 bg-destructive/10 border border-destructive/30 rounded-md">
                            <h3 className="font-display font-semibold text-destructive mb-2 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" /> Danger zone
                            </h3>
                            <p className="text-xs font-app text-muted-foreground mb-3">
                                Wipe all seeded test data (test users, sample listings, sample Joint Ventures, mock case studies) plus all favorites, messages, threads, newsletter subscribers, investor watchlist, and payment transactions.
                                Your <strong>admin account, audit log, and any non-test (real) records are preserved</strong>.
                            </p>
                            <Button onClick={() => setWipeOpen(true)} data-testid="wipe-test-data-btn" variant="destructive" className="font-app">
                                <Trash2 className="h-4 w-4 mr-2" /> Wipe all test data
                            </Button>
                        </div>
                    </Card>

                    {/* Bula Brief weekly digest trigger */}
                    <Card className="p-6 mb-6 bg-gradient-to-r from-fiji-green/5 to-coral/5 border-fiji-green/30 dark:from-fiji-green/10 dark:to-coral/10" data-testid="bula-brief-card">
                        <div className="flex items-start gap-3 mb-4">
                            <Mail className="h-6 w-6 text-fiji-green flex-shrink-0" />
                            <div className="flex-1">
                                <h2 className="font-display text-xl font-bold">Bula Brief — weekly investor digest</h2>
                                <p className="text-sm font-app text-muted-foreground mt-1">
                                    Top 5 newest approved Joint Ventures + a closed-deal headline + the recipient's referral link CTA. Sent to investor watchlist + newsletter subscribers. Currently MOCKED — queues to <code className="px-1 py-0.5 bg-secondary rounded text-xs">db.email_queue</code> with <code className="px-1 py-0.5 bg-secondary rounded text-xs">kind=bula_brief</code>. Drop in your Resend key in <code className="px-1 py-0.5 bg-secondary rounded text-xs">backend/.env</code> to send for real.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                            <Card className="p-3" data-testid="brief-stat-watchlist">
                                <div className="font-display text-2xl font-bold tabular-nums">{bulaBrief?.watchlist_count ?? 0}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Watchlist</div>
                            </Card>
                            <Card className="p-3" data-testid="brief-stat-newsletter">
                                <div className="font-display text-2xl font-bold tabular-nums">{bulaBrief?.newsletter_count ?? 0}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Newsletter</div>
                            </Card>
                            <Card className="p-3" data-testid="brief-stat-jvs">
                                <div className="font-display text-2xl font-bold tabular-nums">{bulaBrief?.top_jvs?.length ?? 0}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Featured Joint Ventures (7d)</div>
                            </Card>
                            <Card className="p-3" data-testid="brief-stat-headline">
                                <div className="font-display text-2xl font-bold tabular-nums">{bulaBrief?.headline ? "1" : "0"}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Headline (30d)</div>
                            </Card>
                        </div>

                        {bulaBrief?.top_jvs?.length > 0 && (
                            <div className="mb-5">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-app mb-2">Preview · top Joint Ventures</div>
                                <div className="space-y-1.5" data-testid="brief-preview-list">
                                    {bulaBrief.top_jvs.map((jv, i) => (
                                        <div key={jv.id || i} className="text-sm font-app flex items-center gap-2">
                                            <span className="text-muted-foreground tabular-nums">{i + 1}.</span>
                                            <span className="font-semibold truncate">{jv.title}</span>
                                            <span className="text-muted-foreground text-xs">· {jv.location_region || jv.location_island || "Fiji"}</span>
                                            <span className="text-fiji-green text-xs ml-auto">{jv.roi_estimate_pct}% ROI</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                            <Button onClick={runBulaBrief} disabled={briefRunning} data-testid="run-bula-brief-btn" className="bg-fiji-green text-white hover:bg-fiji-green/90 font-app">
                                <Mail className="h-4 w-4 mr-2" /> {briefRunning ? "Sending…" : "Send Bula Brief now"}
                            </Button>
                            {bulaBrief?.last_run_at && (
                                <span className="text-xs font-app text-muted-foreground" data-testid="brief-last-run">
                                    Last sent: {new Date(bulaBrief.last_run_at).toLocaleString()}
                                    {bulaBrief?.last_run_by === "system_cron" && (
                                        <span className="ml-1 px-1.5 py-0.5 rounded bg-fiji-green/15 text-fiji-green text-[10px] uppercase font-semibold">auto</span>
                                    )}
                                </span>
                            )}
                        </div>

                        {bulaBrief?.next_scheduled_run && (
                            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2 text-xs font-app" data-testid="brief-schedule-info">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fiji-green/10 text-fiji-green border border-fiji-green/30">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fiji-green opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-fiji-green" />
                                    </span>
                                    <span className="font-semibold uppercase tracking-wider text-[10px]">Auto-send</span>
                                </span>
                                <span className="text-muted-foreground">
                                    {bulaBrief.schedule_label || "Weekly"} · next run{" "}
                                    <span className="font-semibold text-foreground">
                                        {new Date(bulaBrief.next_scheduled_run).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                    </span>
                                </span>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="imagehealth" className="mt-6">
                    <Card className="p-6 bg-fiji-green/5 border-fiji-green/30">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                <ImageIcon className="h-6 w-6 text-fiji-green flex-shrink-0" />
                                <div>
                                    <h2 className="font-display text-xl font-bold">Image Health Check</h2>
                                    <p className="text-sm font-app text-muted-foreground max-w-2xl">
                                        Crawls every external image URL on the site (listings, Joint Ventures, case studies) and flags
                                        any URL that returns 404 or under 1KB — Unsplash IDs occasionally get reassigned or
                                        deleted, silently swapping your hero image for unrelated content.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={runImageHealthCheck}
                                disabled={imageHealthRunning}
                                data-testid="run-image-health-btn"
                                className="bg-ocean-green text-white"
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${imageHealthRunning ? "animate-spin" : ""}`} />
                                {imageHealthRunning ? "Crawling…" : "Run check now"}
                            </Button>
                        </div>

                        {imageHealthLast?.summary && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="image-health-summary">
                                <Card className="p-3">
                                    <div className="font-display text-2xl font-bold">{imageHealthLast.summary.total_references}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Total references</div>
                                </Card>
                                <Card className="p-3">
                                    <div className="font-display text-2xl font-bold text-fiji-green">{imageHealthLast.summary.healthy}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Healthy</div>
                                </Card>
                                <Card className={`p-3 ${imageHealthLast.summary.broken > 0 ? "border-destructive/40" : ""}`}>
                                    <div className={`font-display text-2xl font-bold ${imageHealthLast.summary.broken > 0 ? "text-destructive" : ""}`}>
                                        {imageHealthLast.summary.broken}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Broken</div>
                                </Card>
                                <Card className="p-3">
                                    <div className="font-display text-2xl font-bold">{imageHealthLast.summary.unique_urls}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Unique URLs</div>
                                </Card>
                            </div>
                        )}

                        {imageHealthLast?.summary && (
                            <div className="text-xs font-app text-muted-foreground mb-4">
                                Last checked: {new Date(imageHealthLast.summary.checked_at || imageHealthLast.created_at).toLocaleString()}
                            </div>
                        )}

                        {!imageHealthLast?.summary && (
                            <Card className="p-12 text-center">
                                <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                                <p className="font-app text-muted-foreground">No image health check has been run yet. Click "Run check now" to scan every image.</p>
                            </Card>
                        )}

                        {(imageHealth?.broken || imageHealthLast?.sample_broken)?.length > 0 && (
                            <div className="space-y-3 mt-4" data-testid="image-health-broken-list">
                                <h3 className="font-display text-base font-semibold">Broken / suspicious images</h3>
                                {(imageHealth?.broken || imageHealthLast?.sample_broken || []).map((b, idx) => (
                                    <Card key={`${b.url}-${idx}`} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`broken-image-${idx}`}>
                                        <div className="h-16 w-24 bg-secondary rounded overflow-hidden flex-shrink-0 relative">
                                            <img src={b.url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                            <div className="absolute inset-0 grid place-items-center text-destructive">
                                                <XCircle className="h-6 w-6" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <Badge variant="outline" className="text-[10px] uppercase">{b.source_type}</Badge>
                                                <Badge variant="outline" className="text-[10px] uppercase">{b.field}</Badge>
                                                <Badge className="bg-destructive/15 text-destructive border-0 text-[10px] uppercase">
                                                    {b.kind === "tiny" ? `Tiny (${b.size}B)` : b.kind === "http_error" ? `HTTP ${b.status}` : "Error"}
                                                </Badge>
                                            </div>
                                            <div className="text-sm font-app font-medium truncate">{b.source_title || "(untitled)"}</div>
                                            <div className="text-xs font-app text-muted-foreground truncate">{b.url}</div>
                                        </div>
                                        <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-xs font-app text-ocean dark:text-turquoise hover:underline flex items-center gap-1 flex-shrink-0">
                                            Open <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="agents" className="mt-6">
                    <Card className="p-6 mb-4">
                        <div className="flex items-start gap-3 mb-2">
                            <Briefcase className="h-6 w-6 text-ocean dark:text-turquoise flex-shrink-0" />
                            <div>
                                <h2 className="font-display text-xl font-bold">Agents — verification & featured listings</h2>
                                <p className="text-sm font-app text-muted-foreground max-w-3xl">
                                    Verify agents to put a green "Verified Agent" badge on every one of their listings. Expand any agent
                                    to feature individual listings (Featured listings appear at the top of the marketplace and on the
                                    leaderboard).
                                </p>
                            </div>
                        </div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">
                            {agents.length} agent{agents.length === 1 ? "" : "s"} · {agents.filter(a => a.agent_verified).length} verified
                        </div>
                    </Card>

                    <div className="space-y-3" data-testid="admin-agents-list">
                        {agents.map((a) => (
                            <Card key={a.id} className="p-5" data-testid={`admin-agent-${a.id}`}>
                                <div className="flex items-start gap-4 flex-wrap mb-3">
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-fiji-green to-ocean text-white grid place-items-center font-display font-bold flex-shrink-0">
                                        {(a.full_name || "?").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-app font-semibold">{a.full_name}</div>
                                            {a.agent_verified ? (
                                                <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase font-app">
                                                    <BadgeCheck className="h-3 w-3 mr-0.5" /> Verified
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] uppercase font-app">
                                                    Pending
                                                </Badge>
                                            )}
                                            <Badge variant="outline" className="text-[10px] uppercase font-app">
                                                {a.active_listings} listing{a.active_listings === 1 ? "" : "s"}
                                            </Badge>
                                        </div>
                                        <div className="text-xs font-app text-muted-foreground mt-0.5 truncate">
                                            {a.agency_name || "—"} · {a.email} · {a.phone || "no phone"}
                                        </div>
                                        {a.agent_bio && (
                                            <div className="text-xs font-app text-muted-foreground mt-1 line-clamp-2 max-w-3xl">{a.agent_bio}</div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        {a.agent_verified ? (
                                            <Button onClick={() => verifyAgent(a.id, false)} variant="outline" size="sm" data-testid={`unverify-${a.id}`}>
                                                Unverify
                                            </Button>
                                        ) : (
                                            <Button onClick={() => verifyAgent(a.id, true)} className="bg-fiji-green text-white hover:opacity-90" size="sm" data-testid={`verify-${a.id}`}>
                                                <BadgeCheck className="h-4 w-4 mr-1" /> Verify
                                            </Button>
                                        )}
                                        {a.active_listings > 0 && (
                                            <Button onClick={() => loadAgentListings(a.id)} variant="outline" size="sm" data-testid={`load-listings-${a.id}`}>
                                                <Building2 className="h-4 w-4 mr-1" /> Listings
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {agentListings[a.id]?.length > 0 && (
                                    <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid={`agent-listings-${a.id}`}>
                                        {agentListings[a.id].map((li) => (
                                            <div key={li.id} className="flex items-center gap-3 p-2 rounded border border-border bg-card">
                                                <div className="h-12 w-16 bg-secondary rounded overflow-hidden flex-shrink-0">
                                                    <img src={li.external_photos?.[0] || ""} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-app text-sm font-medium truncate">{li.title}</div>
                                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">{fmtFJD(li.price_fjd)} · {li.land_size_acres}ac</div>
                                                </div>
                                                <Button onClick={() => featureListing(li.id, !li.is_featured)} variant={li.is_featured ? "default" : "outline"} size="sm" className={li.is_featured ? "bg-coral text-white" : ""} data-testid={`feature-${li.id}`}>
                                                    <Star className={`h-3.5 w-3.5 ${li.is_featured ? "fill-white" : ""}`} />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        ))}
                        {agents.length === 0 && (
                            <Card className="p-12 text-center">
                                <p className="font-app text-muted-foreground">No agents yet. Encourage agents to register at /register.</p>
                            </Card>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="matcher" className="mt-6">
                    <Card className="p-6 mb-4">
                        <div className="flex items-start gap-3 mb-4">
                            <Sparkles className="h-6 w-6 text-coral flex-shrink-0" />
                            <div>
                                <h2 className="font-display text-xl font-bold">Match qualified investors</h2>
                                <p className="text-sm font-app text-muted-foreground max-w-3xl">
                                    Paste any approved Joint Venture / listing / business ID. The matcher scores every investor in the database
                                    against the asset using interest tags, capital range, geo focus, accreditation, and risk profile —
                                    then ranks the top matches with the reasons.
                                </p>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Asset type</label>
                                <Select value={matcher.asset_type} onValueChange={(v) => setMatcher((m) => ({ ...m, asset_type: v }))}>
                                    <SelectTrigger className="mt-1.5" data-testid="match-asset-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="jv">Joint Venture opportunity</SelectItem>
                                        <SelectItem value="listing">Land listing</SelectItem>
                                        <SelectItem value="business">Business</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2 flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Asset ID</label>
                                    <Input
                                        className="mt-1.5"
                                        placeholder="UUID of the Joint Venture / listing / business"
                                        value={matcher.asset_id}
                                        onChange={(e) => setMatcher((m) => ({ ...m, asset_id: e.target.value }))}
                                        data-testid="match-asset-id"
                                    />
                                </div>
                                <Button onClick={runMatcher} disabled={matcher.loading} className="bg-coral text-white" data-testid="match-run-btn">
                                    <Sparkles className="h-4 w-4 mr-1" /> {matcher.loading ? "Matching…" : "Match"}
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {matcher.result && (
                        <div data-testid="match-results">
                            <Card className="p-4 mb-3 bg-fiji-green/5 border-fiji-green/30">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    <div className="font-display font-bold text-lg">{matcher.result.asset.title}</div>
                                    <Badge variant="outline" className="text-[10px] uppercase">{matcher.result.asset.asset_type}</Badge>
                                    {matcher.result.asset.location_island && <span className="text-xs font-app text-muted-foreground">{matcher.result.asset.location_island}</span>}
                                    <span className="ml-auto text-xs font-app text-muted-foreground">
                                        <span className="font-display font-bold text-foreground">{matcher.result.total_matched}</span> matches
                                    </span>
                                </div>
                            </Card>
                            <div className="space-y-2">
                                {matcher.result.matches.map((m, idx) => (
                                    <Card key={m.investor.id} className="p-4" data-testid={`match-row-${idx}`}>
                                        <div className="flex items-start gap-3 flex-wrap">
                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-coral to-fiji-green text-white grid place-items-center font-display font-bold flex-shrink-0">
                                                {(m.investor.full_name || "?").slice(0, 1).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="font-app font-semibold">{m.investor.full_name}</div>
                                                    {m.investor.accredited && (
                                                        <Badge className="bg-coral/15 text-coral border-0 text-[10px] uppercase font-app">Accredited</Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs font-app text-muted-foreground mt-0.5">
                                                    {m.investor.email} · capital {m.investor.capital_min_fjd != null ? `FJ$${m.investor.capital_min_fjd.toLocaleString()}` : "—"}–{m.investor.capital_max_fjd != null ? `FJ$${m.investor.capital_max_fjd.toLocaleString()}` : "—"}
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {(m.reasons || []).map((r, i) => (
                                                        <Badge key={i} variant="outline" className="text-[10px] font-app">{r}</Badge>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="font-display text-2xl font-bold text-coral tabular-nums">{m.score}</div>
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">match score</div>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                                {matcher.result.matches.length === 0 && (
                                    <Card className="p-12 text-center"><p className="font-app text-muted-foreground">No matching investors found.</p></Card>
                                )}
                            </div>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="deals" className="mt-6">
                    <Card className="p-5 mb-4 bg-gradient-to-r from-fiji-green/10 to-coral/10 border-fiji-green/30">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                                <Handshake className="h-5 w-5 text-fiji-green" />
                                <div>
                                    <h2 className="font-display text-lg font-bold">Deal Pipeline & Monetization</h2>
                                    <p className="font-app text-sm text-muted-foreground">Track every introduced match through diligence, close, and fee collection.</p>
                                </div>
                            </div>
                            <Button onClick={() => setCreateDealOpen(true)} data-testid="create-deal-btn" className="bg-fiji-green text-white hover:bg-fiji-green/90">
                                <Plus className="h-4 w-4 mr-1.5" /> New deal
                            </Button>
                        </div>
                    </Card>

                    {/* Top stat tiles */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="deal-pipeline-stats">
                        <Card className="p-4">
                            <Banknote className="h-4 w-4 text-fiji-green mb-1" />
                            <div className="font-display text-2xl font-bold tabular-nums" data-testid="stat-fees-earned">{fmtFJD(dealPipeline?.fees_earned_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Fees earned (paid)</div>
                        </Card>
                        <Card className="p-4">
                            <Banknote className="h-4 w-4 text-coral mb-1" />
                            <div className="font-display text-2xl font-bold tabular-nums" data-testid="stat-fees-receivable">{fmtFJD(dealPipeline?.fees_receivable_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Fees receivable (closed)</div>
                        </Card>
                        <Card className="p-4">
                            <Banknote className="h-4 w-4 text-ocean dark:text-turquoise mb-1" />
                            <div className="font-display text-2xl font-bold tabular-nums" data-testid="stat-fees-future">{fmtFJD(dealPipeline?.fees_future_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Fees in pipeline (intro+diligence)</div>
                        </Card>
                        <Card className="p-4">
                            <Handshake className="h-4 w-4 text-fiji-green mb-1" />
                            <div className="font-display text-2xl font-bold tabular-nums" data-testid="stat-pipeline-value">{fmtFJD(dealPipeline?.pipeline_value_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Active pipeline value</div>
                        </Card>
                    </div>

                    {/* Status filter pills */}
                    <div className="flex gap-2 flex-wrap mb-4" data-testid="deal-status-filter">
                        {[
                            { id: "all", label: `All`, count: dealPipeline?.counts?.total ?? 0 },
                            { id: "introduced", label: "Introduced", count: dealPipeline?.counts?.introduced ?? 0 },
                            { id: "in_diligence", label: "In Diligence", count: dealPipeline?.counts?.in_diligence ?? 0 },
                            { id: "closed", label: "Closed", count: dealPipeline?.counts?.closed ?? 0 },
                            { id: "paid", label: "Paid", count: dealPipeline?.counts?.paid ?? 0 },
                            { id: "dead", label: "Dead", count: dealPipeline?.counts?.dead ?? 0 },
                        ].map((s) => (
                            <Button
                                key={s.id}
                                data-testid={`deal-filter-${s.id}`}
                                onClick={() => setDealStatusFilter(s.id)}
                                variant={dealStatusFilter === s.id ? "default" : "outline"}
                                size="sm"
                                className={dealStatusFilter === s.id ? "bg-fiji-green text-white hover:bg-fiji-green/90" : "font-app"}
                            >
                                {s.label} ({s.count})
                            </Button>
                        ))}
                    </div>

                    {/* Deals list */}
                    <div className="space-y-3" data-testid="deals-list">
                        {deals.length === 0 ? (
                            <Card className="p-12 text-center">
                                <Handshake className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                                <p className="font-app text-muted-foreground">No deals yet. Click "New deal" above to log your first introduction.</p>
                            </Card>
                        ) : deals.map((d) => (
                            <Card key={d.id} data-testid={`deal-card-${d.id}`} className="p-5">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            <Badge className={
                                                d.status === "paid" ? "bg-fiji-green/15 text-fiji-green border-0 uppercase text-[10px]" :
                                                d.status === "closed" ? "bg-coral/15 text-coral border-0 uppercase text-[10px]" :
                                                d.status === "in_diligence" ? "bg-ocean/15 text-ocean dark:bg-turquoise/15 dark:text-turquoise border-0 uppercase text-[10px]" :
                                                d.status === "dead" ? "bg-muted text-muted-foreground border-0 uppercase text-[10px]" :
                                                "bg-secondary text-foreground border-0 uppercase text-[10px]"
                                            }>{d.status.replace("_", " ")}</Badge>
                                            <Badge variant="outline" className="text-[10px] uppercase">{d.asset_type}</Badge>
                                            <Badge variant="outline" className="text-[10px] uppercase">via {d.intro_source.replace("_", " ")}</Badge>
                                        </div>
                                        <h3 className="font-display text-lg font-bold mb-1">{d.title}</h3>
                                        <div className="text-xs font-app text-muted-foreground space-y-0.5">
                                            {d.seller_name && <div>Seller: <span className="text-foreground">{d.seller_name}</span></div>}
                                            {d.buyer_name && <div>Buyer: <span className="text-foreground">{d.buyer_name}</span></div>}
                                            <div>Value: <span className="text-foreground font-semibold">{fmtFJD(d.deal_value_fjd)}</span> · Fee: {d.agreed_fee_pct}% = <span className="text-fiji-green font-semibold">{fmtFJD(d.agreed_fee_fjd)}</span></div>
                                            <div>Created {new Date(d.created_at).toLocaleDateString()} {d.closed_at ? `· Closed ${new Date(d.closed_at).toLocaleDateString()}` : ""} {d.paid_at ? `· Paid ${new Date(d.paid_at).toLocaleDateString()}` : ""}</div>
                                        </div>
                                        {d.notes && <p className="font-app text-sm mt-2 text-muted-foreground line-clamp-2">{d.notes}</p>}
                                    </div>
                                    <div className="flex flex-col gap-2 items-end">
                                        <Select value={d.status} onValueChange={(v) => updateDealStatus(d.id, v)}>
                                            <SelectTrigger data-testid={`deal-status-${d.id}`} className="w-[160px] h-9 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="introduced">Introduced</SelectItem>
                                                <SelectItem value="in_diligence">In Diligence</SelectItem>
                                                <SelectItem value="closed">Closed</SelectItem>
                                                <SelectItem value="paid">Paid</SelectItem>
                                                <SelectItem value="dead">Dead</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button data-testid={`deal-delete-${d.id}`} onClick={() => deleteDeal(d.id)} variant="ghost" size="sm" className="text-destructive hover:text-destructive h-8">
                                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="pricing" className="mt-6">
                    <AdminPricingTab />
                </TabsContent>

                <TabsContent value="contacts" className="mt-6">
                    <AdminContactsTab />
                </TabsContent>

                <TabsContent value="pipelines" className="mt-6">
                    <AdminInvestorPipelinesTab />
                </TabsContent>

                <TabsContent value="concierge" className="mt-6">
                    <AdminConciergePurchasesTab />
                </TabsContent>

                <TabsContent value="tltb" className="mt-6">
                    <AdminTLTBContactsTab />
                </TabsContent>

                <TabsContent value="tltb-analytics" className="mt-6">
                    <AdminTLTBShareAnalyticsTab />
                </TabsContent>

                <TabsContent value="payouts" className="mt-6">
                    <AdminPayoutsTab />
                </TabsContent>

                <TabsContent value="settings" className="mt-6">
                    <AdminPlatformSettingsTab />
                </TabsContent>
            </Tabs>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display">Vet Joint Venture: {active?.title}</DialogTitle>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-4">
                            <div className="bg-secondary/50 rounded-md p-4 text-sm font-app space-y-1">
                                <div><strong>Vision:</strong> {active.project_vision}</div>
                                <div><strong>Financials:</strong> {active.financial_projections}</div>
                                <div><strong>Capital:</strong> {fmtFJD(active.capital_required_fjd)} · <strong>ROI:</strong> {active.roi_estimate_pct}% · <strong>Equity:</strong> {active.landowner_equity_pct}%</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Decision</Label>
                                    <select data-testid="vet-decision-select" value={decision.decision} onChange={(e) => setDecision({ ...decision, decision: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm font-app">
                                        <option value="approved">Approve</option>
                                        <option value="rejected">Reject</option>
                                        <option value="info_requested">Request more info</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Admin score (0-100)</Label>
                                    <Input data-testid="vet-score-input" type="number" min={0} max={100} value={decision.score} onChange={(e) => setDecision({ ...decision, score: parseInt(e.target.value) || 0 })} className="mt-1.5" />
                                </div>
                            </div>
                            <div>
                                <Label>Comment / rationale</Label>
                                <Textarea data-testid="vet-comment-input" value={decision.comment} onChange={(e) => setDecision({ ...decision, comment: e.target.value })} rows={4} className="mt-1.5" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setActive(null)}>Cancel</Button>
                        <Button onClick={submitDecision} data-testid="submit-decision-btn" className="bg-ocean-green text-white">Submit decision</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Magic Link / Lead Won Automation Modal */}
            <Dialog open={!!magicLinkModal} onOpenChange={(o) => !o && setMagicLinkModal(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-coral" /> Lead Won — automation complete
                        </DialogTitle>
                        <DialogDescription className="font-app pt-2">
                            We've automatically created a seller account for <strong>{magicLinkModal?.lead_name}</strong>{magicLinkModal?.user_created ? " (new user)" : ""} and a draft Joint Venture pre-filled with their land details.
                            Send them this magic-link to log in instantly.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="bg-secondary/40 rounded-md p-3 font-mono text-xs break-all" data-testid="magic-link-display">
                            {magicLinkModal?.magic_link}
                        </div>
                        <div className="text-xs font-app text-muted-foreground">
                            Expires {magicLinkModal?.expires_at ? new Date(magicLinkModal.expires_at).toLocaleString() : "in 14 days"}. A mock email was logged to backend (replace with real email integration before production).
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setMagicLinkModal(null)}>Close</Button>
                        <Button
                            data-testid="copy-magic-link-btn"
                            onClick={() => {
                                navigator.clipboard.writeText(magicLinkModal?.magic_link || "");
                                toast.success("Link copied to clipboard");
                            }}
                            className="bg-coral text-white hover:bg-coral-deep"
                        >
                            Copy magic link
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={createDealOpen} onOpenChange={setCreateDealOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Handshake className="h-5 w-5 text-fiji-green" /> Log a new deal
                        </DialogTitle>
                        <DialogDescription className="font-app pt-1">
                            Track every introduction so you know exactly where your fees are coming from.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input data-testid="deal-form-title" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} placeholder="e.g. Coral Coast Eco-Resort Joint Venture" className="mt-1.5" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Asset type</Label>
                                <Select value={dealForm.asset_type} onValueChange={(v) => setDealForm({ ...dealForm, asset_type: v })}>
                                    <SelectTrigger data-testid="deal-form-asset-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="jv">Joint Venture opportunity</SelectItem>
                                        <SelectItem value="listing">Land listing</SelectItem>
                                        <SelectItem value="business">Business sale</SelectItem>
                                        <SelectItem value="consultation">Consultation</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Intro source</Label>
                                <Select value={dealForm.intro_source} onValueChange={(v) => setDealForm({ ...dealForm, intro_source: v })}>
                                    <SelectTrigger data-testid="deal-form-intro-source" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="platform_match">Platform match</SelectItem>
                                        <SelectItem value="consultation">Consultation form</SelectItem>
                                        <SelectItem value="agent_referral">Agent referral</SelectItem>
                                        <SelectItem value="partner_referral">Partner referral</SelectItem>
                                        <SelectItem value="manual">Manual</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Asset ID (optional)</Label>
                            <Input data-testid="deal-form-asset-id" value={dealForm.asset_id} onChange={(e) => setDealForm({ ...dealForm, asset_id: e.target.value })} placeholder="Joint Venture / listing / business UUID for cross-link" className="mt-1.5 font-mono text-xs" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Seller name</Label>
                                <Input data-testid="deal-form-seller" value={dealForm.seller_name} onChange={(e) => setDealForm({ ...dealForm, seller_name: e.target.value })} placeholder="Landowner / business seller" className="mt-1.5" />
                            </div>
                            <div>
                                <Label>Buyer name</Label>
                                <Input data-testid="deal-form-buyer" value={dealForm.buyer_name} onChange={(e) => setDealForm({ ...dealForm, buyer_name: e.target.value })} placeholder="Investor / acquirer" className="mt-1.5" />
                            </div>
                        </div>
                        <div>
                            <Label>Region <span className="text-muted-foreground font-normal text-xs">(shown anonymously on the public Deal Wins ticker — e.g. "Savusavu", "Viti Levu")</span></Label>
                            <Input data-testid="deal-form-region" value={dealForm.region} onChange={(e) => setDealForm({ ...dealForm, region: e.target.value })} placeholder="Savusavu, Viti Levu, Coral Coast…" className="mt-1.5" />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <Label>Deal value (FJD)</Label>
                                <Input data-testid="deal-form-value" type="number" min={0} value={dealForm.deal_value_fjd} onChange={(e) => setDealForm({ ...dealForm, deal_value_fjd: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label>Fee %</Label>
                                <Input data-testid="deal-form-fee-pct" type="number" min={0} max={100} step="0.5" value={dealForm.agreed_fee_pct} onChange={(e) => setDealForm({ ...dealForm, agreed_fee_pct: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={dealForm.status} onValueChange={(v) => setDealForm({ ...dealForm, status: v })}>
                                    <SelectTrigger data-testid="deal-form-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="introduced">Introduced</SelectItem>
                                        <SelectItem value="in_diligence">In Diligence</SelectItem>
                                        <SelectItem value="closed">Closed</SelectItem>
                                        <SelectItem value="paid">Paid</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Fee override (FJD) <span className="text-muted-foreground font-normal text-xs">(optional — leave blank to use auto-computed % above)</span></Label>
                            <Input data-testid="deal-form-fee-override" type="number" min={0} value={dealForm.agreed_fee_override} onChange={(e) => setDealForm({ ...dealForm, agreed_fee_override: e.target.value })} placeholder="e.g. 25000 for a flat-fee deal" className="mt-1.5" />
                        </div>                        <div className="text-xs font-app text-muted-foreground bg-secondary/50 rounded-md p-2.5" data-testid="deal-form-fee-preview">
                            {String(dealForm.agreed_fee_override).trim() !== "" ? (
                                <>Override fee: <span className="font-bold text-foreground">{fmtFJD(parseFloat(dealForm.agreed_fee_override) || 0)}</span> <span className="text-muted-foreground/80">(% ignored)</span></>
                            ) : (
                                <>Computed fee: <span className="font-bold text-foreground">{fmtFJD(((parseFloat(dealForm.deal_value_fjd) || 0) * (parseFloat(dealForm.agreed_fee_pct) || 0)) / 100)}</span></>
                            )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                            <div>
                                <Label>Referrer user ID <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                                <Input data-testid="deal-form-referrer-id" value={dealForm.referrer_user_id} onChange={(e) => setDealForm({ ...dealForm, referrer_user_id: e.target.value })} placeholder="UUID of the user who introduced this deal" className="mt-1.5 font-mono text-xs" />
                            </div>
                            <div>
                                <Label>Referrer share %</Label>
                                <Input data-testid="deal-form-referrer-share" type="number" min={0} max={100} step="0.5" value={dealForm.referrer_share_pct} onChange={(e) => setDealForm({ ...dealForm, referrer_share_pct: e.target.value })} placeholder="e.g. 10 = 10% of the fee" className="mt-1.5" />
                            </div>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea data-testid="deal-form-notes" value={dealForm.notes} onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })} rows={3} placeholder="Context, terms, contacts…" className="mt-1.5" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateDealOpen(false)}>Cancel</Button>
                        <Button onClick={submitDeal} disabled={creatingDeal} data-testid="submit-deal-btn" className="bg-fiji-green text-white hover:bg-fiji-green/90">
                            {creatingDeal ? "Creating…" : "Create deal"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" /> Wipe all test data?
                        </DialogTitle>
                        <DialogDescription className="font-app pt-2">
                            This permanently deletes all seeded test users, listings, Joint Venture opportunities, mock case studies, plus all favorites, messages, threads, newsletter subscribers, investor watchlist, and payment transactions. <strong>The admin account, audit log, and any non-test (real) records are preserved.</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="font-app text-sm">Type <code className="px-1.5 py-0.5 bg-secondary rounded text-xs font-bold">WIPE</code> to confirm</Label>
                        <Input data-testid="wipe-confirm-input" value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)} placeholder="WIPE" className="font-mono" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setWipeOpen(false); setWipeConfirm(""); }}>Cancel</Button>
                        <Button onClick={wipeTestData} disabled={wiping || wipeConfirm !== "WIPE"} data-testid="confirm-wipe-btn" variant="destructive" className="font-app">
                            {wiping ? "Wiping…" : <><Trash2 className="h-4 w-4 mr-2" /> Confirm wipe</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
